import { getConfig } from '@/config/config.ts';
import type { LegalEntity } from '@/model/LegalEntity.ts';
import type { Root } from '@/model/Root.ts';
import type { RootDetails, RootDetailsAndFailures } from '@/model/RootDetails.ts';
import type { RootFees } from '@/model/RootFees.ts';
import type { RootPortfolioSummary } from '@/model/RootPortfolioSummary.ts';
import type { AlternativesPosition } from '@/model/detailedValuation/AlternativesPosition.ts';
import type { BondPosition } from '@/model/detailedValuation/BondPosition.ts';
import type { CashPosition } from '@/model/detailedValuation/CashPosition.ts';
import type { EquityPosition } from '@/model/detailedValuation/EquityPosition.ts';
import type { FxForwardPosition } from '@/model/detailedValuation/FxForwardPosition.ts';
import type { OthersPosition } from '@/model/detailedValuation/OthersPosition.ts';
import type { PositionsGroupByCurrencyPair } from '@/model/detailedValuation/PositionsGroup.ts';
import type { RootDetailedValuation } from '@/model/detailedValuation/RootDetailedValuation.ts';
import type { RootAuthorisation } from '@/store/authorisations/model.ts';
import type {
  AccountFutureOperations,
  BooksOrder,
  RootOperation,
  RootOperationType,
  SigmaOperation,
} from '@/store/operations/model.ts';
import type { RootParty } from '@/store/parties/model.ts';
import type { RootWarranty } from '@/store/warranties/model.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { WebApiResponseWithFailures } from './WebApiResponse.ts';
import { webApiResponseBaseQuery } from './webApiResponseBaseQuery.ts';
import type { RootPortfolioUnusedCommitmentSummary } from '@/model/RootUnusedCommitmentSummary.ts';
import type { RootPortfolioUnusedCommitment } from '@/model/RootPortfolioUnusedCommitment.ts';

export type SearchParams = {
  searchTerm: string;
  all?: boolean;
  entity: LegalEntity;
};

type DetailedValuationParameters = {
  rootId: string;
  valuationCurrency: string;
};

type DetailedPositionRequestParameters = {
  rootId: string;
  currency: string; // valuation currency
  isin: string;
};

export type RootOperationsParameters = {
  rootId: string;
  assetType?: string;
  maxValueDate?: string;
  instrumentCode?: string;
  fromDate?: string;
  toDate?: string;
  pageSize?: number;
  pageNumber?: number;
};

const prefixUrl = getConfig().prefixUrl ?? '';

export const rootApi = createApi({
  reducerPath: 'rootApi',
  keepUnusedDataFor: 3_600, // By defaut keep query data 1h even if unused
  baseQuery: webApiResponseBaseQuery<unknown>,
  endpoints: (builder) => ({
    searchRoots: builder.query<Root[], SearchParams>({
      query: ({ searchTerm, all = false, entity }) => ({
        method: 'POST',
        url: `${prefixUrl}/roots`,
        body: { searchValue: searchTerm, all },
        params: {
          legalEntity: entity,
        },
      }),
      keepUnusedDataFor: 30, // keep unused results for 30s in cache
      transformResponse: (response: WebApiResponseWithFailures<Root>): Root[] => {
        const rootsCopy = [...response.data];
        rootsCopy.sort((a: Root) => (a.rootStatus === 'Ac' ? -1 : 1)); // Actif status should be first
        return rootsCopy;
      },
    }),

    getRoot: builder.query<RootDetailsAndFailures, string>({
      query: (id) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}`,
      }),
      transformResponse: (response: WebApiResponseWithFailures<RootDetails>) => ({
        root: response.data[0],
        failures: response.failures,
      }),
    }),

    getRootPortfolioSummary: builder.query<
      RootPortfolioSummary,
      { id: string; valuationCurrency?: string }
    >({
      query: ({ id, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}/summaries`,
        params: {
          evalCurrency: valuationCurrency,
        },
      }),
      transformResponse: ({ data: [summary] }: WebApiResponseWithFailures<RootPortfolioSummary>) => summary as RootPortfolioSummary,
    }),
    getRootUnusedCommitmentSummary: builder.query<RootPortfolioUnusedCommitmentSummary, {rootId: string}>({
      query: ({rootId}) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/undrawn-commitments/summaries`,
      }),
      transformResponse: (response : WebApiResponseWithFailures<RootPortfolioUnusedCommitmentSummary>) => response.data[0] as RootPortfolioUnusedCommitmentSummary,
    }),
    getRootUnusedCommitment: builder.query<RootPortfolioUnusedCommitment[], {rootId: string}>({
      query: ({rootId}) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/undrawn-commitments`,
      }),
      transformResponse: ({data}: WebApiResponseWithFailures<RootPortfolioUnusedCommitment>) => data,
    }),
    getRootDetailedValuationsForCash: builder.query<
      RootDetailedValuation<CashPosition>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/cashs/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<RootDetailedValuation<CashPosition>>) => summary,
    }),

    getRootDetailedValuationsForEquities: builder.query<
      RootDetailedValuation<EquityPosition>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/equities/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<RootDetailedValuation<EquityPosition>>) => summary,
    }),

    getRootDetailedValuationsForBonds: builder.query<
      RootDetailedValuation<BondPosition>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/bonds/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<RootDetailedValuation<BondPosition>>) => summary,
    }),

    getRootDetailedValuationsForFxForwards: builder.query<
      RootDetailedValuation<FxForwardPosition>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/forwards/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<
        RootDetailedValuation<FxForwardPosition>
      >) => summary,
    }),

    getRootDetailedValuationsForOthers: builder.query<
      RootDetailedValuation<Partial<OthersPosition>>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/others/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<RootDetailedValuation<Partial<OthersPosition>>>) => summary,
    }),

    getRootDetailedValuationsForAlternatives: builder.query<
      RootDetailedValuation<Partial<AlternativesPosition>>,
      DetailedValuationParameters
    >({
      query: ({ rootId, valuationCurrency }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/alternatives/${valuationCurrency}`,
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<RootDetailedValuation<Partial<AlternativesPosition>>>) =>
        summary,
    }),

    getRootOperations: builder.query<RootOperationType[], RootOperationsParameters>({
      query: ({
        rootId,
        assetType,
        instrumentCode,
        fromDate,
        toDate,
        pageSize = 100,
        pageNumber = 1,
      }) => ({
        method: 'POST',
        url: `${prefixUrl}/roots/${rootId}/operations`,
        body: {
          assetType,
          instrumentCode,
          fromDate,
          toDate,
          pageSize,
          pageNumber,
        },
      }),
      transformResponse: ({ data: operations }) => operations as RootOperationType[],
    }),
    getRootOperationsFuture: builder.query<AccountFutureOperations[], RootOperationsParameters>({
      query: ({ rootId, maxValueDate }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/operations/futures?maxValueDate=${maxValueDate}`,
      }),
      transformResponse: ({ data: operations }) => operations as AccountFutureOperations[],
    }),
    getOrderBooks: builder.query<BooksOrder[], {rootId: string}>({
      query: ({ rootId }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/order-books`,
      }),
      transformResponse: ({ data: booksOrder }) => booksOrder as BooksOrder[],
    }),
    getRootFees: builder.query<RootFees[], string>({
      query: (id) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}/fees`,
        params: {
          pageSize: 50,
        },
      }),
      transformResponse: ({ data: operations }) => operations as RootFees[],
    }),
    getSigmaOperations: builder.query<SigmaOperation[], {rootId: string}>({
      query: ({ rootId }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/sigma-operations`,
      }),
      transformResponse: ({ data }) => data as SigmaOperation[],
    }),

    getRootAuthorisations: builder.query<RootAuthorisation[], string>({
      query: (id) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}/authorisations`,
      }),
      transformResponse: ({ data: operations }) => operations as RootAuthorisation[],
    }),

    getRootWarranties: builder.query<RootWarranty[], string>({
      query: (id) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}/warranties`,
      }),
      transformResponse: ({ data: operations }) => operations as RootWarranty[],
    }),

    getRootParties: builder.query<RootParty[], string>({
      query: (id) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${id}/parties`,
      }),
      transformResponse: ({ data: operations }) => operations as RootParty[],
    }),

    getCashOperationDetails: builder.query<CashPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/cashs`,
        params: {
          isin, currency
        }
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<CashPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
    getBondOperationDetails: builder.query<BondPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/bonds`,
        params: {
          isin,
          currency
        }
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<BondPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
    getEquityOperationDetails: builder.query<EquityPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/equities`,
        params: {
          isin,
          currency
        }
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<EquityPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
    getFxForwardOperationDetails: builder.query<FxForwardPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/forwards`,
        params: {
          isin,
          currency
        }
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<FxForwardPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
    getAlternativeOperationDetails: builder.query<AlternativesPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/alternatives`,
        params: {
          isin,
          currency
        },
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<AlternativesPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
    getOtherOperationDetails: builder.query<OthersPosition, DetailedPositionRequestParameters
    >({
      query: ({ rootId, currency, isin }) => ({
        method: 'GET',
        url: `${prefixUrl}/roots/${rootId}/others`,
        params: {
          isin,
          currency
        }
      }),
      transformResponse: ({
        data: [summary],
      }: WebApiResponseWithFailures<OthersPosition>) => summary,
      keepUnusedDataFor: 5,
    }),
  }),
});

export const {
  useSearchRootsQuery,
  useLazySearchRootsQuery,
  useGetRootQuery,
  useLazyGetRootQuery,
  useGetRootPortfolioSummaryQuery,
  useGetRootUnusedCommitmentSummaryQuery,
  useGetRootUnusedCommitmentQuery,
  useGetRootDetailedValuationsForEquitiesQuery,
  useGetRootDetailedValuationsForCashQuery,
  useGetRootDetailedValuationsForBondsQuery,
  useGetRootDetailedValuationsForFxForwardsQuery,
  useGetRootOperationsQuery,
  useLazyGetRootOperationsQuery,
  useGetRootOperationsFutureQuery,
  useLazyGetRootOperationsFutureQuery,
  useLazyGetOrderBooksQuery,
  useGetOrderBooksQuery,
  useGetSigmaOperationsQuery,
  useGetRootFeesQuery,
  useGetRootAuthorisationsQuery,
  useGetRootWarrantiesQuery,
  useGetRootPartiesQuery,
  useGetRootDetailedValuationsForAlternativesQuery,
  useGetRootDetailedValuationsForOthersQuery,
  useGetCashOperationDetailsQuery,
  useGetBondOperationDetailsQuery,
  useGetEquityOperationDetailsQuery,
  useGetOtherOperationDetailsQuery,
  useGetAlternativeOperationDetailsQuery,
  useGetFxForwardOperationDetailsQuery,
} = rootApi;
